import {getISOTimeString} from '@/utils/commons'
import {TARGET_TYPES} from '@/utils/constants/saleTargetConstants'
import {Constants} from '@/utils/constants/commonConstants'

/**
 * Creates a sales target request object based on the target type and data
 * @param {Object} salesTargetData - The sales target data from the store
 * @param {boolean} isPrerequisiteTarget - Whether this is a prerequisite target
 * @returns {Object} The formatted request object
 */
export const createSalesTargetRequestObject = (salesTargetData, isPrerequisiteTarget) => {
  const isLayeredTarget =
    salesTargetData?.targetType === TARGET_TYPES.ProductTargetLayered ||
    salesTargetData?.targetType === TARGET_TYPES.MonthlyTargetLayered

  const isMainTargetOrProdTarget =
    salesTargetData?.targetType === TARGET_TYPES.MainTarget ||
    salesTargetData?.targetType === TARGET_TYPES.ProductTarget

  // Common properties shared between both types
  const commonProperties = {
    targetName: salesTargetData?.targetName || '',
    displayContent: {
      en: salesTargetData?.displayContent?.en,
      es_MX: salesTargetData?.displayContent?.es,
    },
    targetType: salesTargetData?.targetTypeValue || '',
    joinCampaignFlag: salesTargetData?.joinCampaignFlag,
    VATIncluded: salesTargetData?.VATIncluded,
    roundPointsValue: salesTargetData?.roundPointsValue || '',
    roundUpPointsFlag: salesTargetData?.roundUpPointsFlag ?? false,
    offlineOrdersIncluded: salesTargetData?.offlineOrdersIncluded ?? false,
    cepRefId: salesTargetData?.cepRefId ?? null,
    displayStartDate: getISOTimeString(salesTargetData?.displayStartDate, false, true),
    displayEndDate: getISOTimeString(salesTargetData?.displayEndDate, true, false),
    activeStartDate: getISOTimeString(salesTargetData?.activeStartDate, false, true),
    activeEndDate: getISOTimeString(salesTargetData?.activeEndDate, true, false),
    targetDeliveryDate: getISOTimeString(
      salesTargetData?.targetEndDate || salesTargetData?.targetDeliveryDate,
      true,
      false
    ),
    pointsDeliveryDate: getISOTimeString(salesTargetData?.pointsDeliveryDate, true, false),
    media: salesTargetData?.joinCampaignFlag ? salesTargetData.media : [],
    storeList: salesTargetData?.storeList || '',
    metadata: {
      productFileName:
        salesTargetData?.selectedType === Constants.UploadedProducts
          ? salesTargetData?.tabWiseData?.parent?.mediaFile[0]
          : null,
      isProductIdsUploaded: salesTargetData?.selectedType === Constants.UploadedProducts,
    },
  }

  // If it's not a prerequisite target, return with specific properties
  if (!isPrerequisiteTarget) {
    return {
      ...commonProperties,
      ignoreSkuList: salesTargetData?.tabWiseData?.parent?.ignoreSkuList,
      pointDisplay: isLayeredTarget ? salesTargetData?.pointDisplay : null,
      ruleSet: salesTargetData?.tabWiseData?.parent?.ruleSet,
      childRuleSet: salesTargetData?.childRuleSet,
      status: salesTargetData?.status,
      awardPointsImmedFlag: isMainTargetOrProdTarget ? salesTargetData?.awardPointsImmedFlag : null,
    }
  }

  // If it's a prerequisite target, return with child object
  return {
    ...commonProperties,
    ruleSet: salesTargetData?.tabWiseData?.parent?.ruleSet,
    awardPointsImmedFlag: salesTargetData?.awardPointsImmedFlag ?? false,
    childRuleSet: null,
    ignoreSkuList: salesTargetData?.tabWiseData?.child?.ignoreSkuList ?? false,
    pointDisplay: null,
    child: {
      id: '',
      storeList: '',
      targetName: salesTargetData?.targetName || '',
      ruleSet: salesTargetData?.tabWiseData?.child?.ruleSet,
      displayContent: {
        es_MX: salesTargetData?.child?.displayContent?.es,
        en: salesTargetData?.child?.displayContent?.en,
      },
      targetType: salesTargetData?.targetTypeValue || '',
      awardPointsImmedFlag: salesTargetData?.awardPointsImmedFlag ?? false,
      joinCampaignFlag: true,
      VATIncluded: true,
      cepRefId: salesTargetData?.cepRefId ?? null,
      childRuleSet: null,
      roundPointsValue: salesTargetData?.roundPointsValue || '',
      roundUpPointsFlag: salesTargetData?.roundUpPointsFlag ?? false,
      activeStartDate: getISOTimeString(salesTargetData?.activeStartDate, false, true),
      activeEndDate: getISOTimeString(salesTargetData?.activeEndDate, false, true),
      targetDeliveryDate: getISOTimeString(salesTargetData?.targetDeliveryDate, false, true),
      displayStartDate: getISOTimeString(salesTargetData?.displayStartDate, false, true),
      displayEndDate: getISOTimeString(salesTargetData?.displayEndDate, false, true),
      media: salesTargetData?.joinCampaignFlag ? salesTargetData.media : [],
      status: salesTargetData?.status,
      ignoreSkuList: salesTargetData?.tabWiseData?.parent?.ignoreSkuList ?? false,
      offlineOrdersIncluded: salesTargetData?.offlineOrdersIncluded ?? false,
      pointDisplay: null,
      children: [],
      application: {
        createdAt: '',
        updatedAt: '',
        createdBy: null,
        updatedBy: null,
        id: '',
        country: '',
        siteId: '',
        timezone: '',
        pointsMultiplierRuleSet: {
          rules: [
            {
              name: 'Order Channel',
              matchWith: ['online'],
              multiplier: 2.0,
            },
            {
              name: 'Order Channel',
              matchWith: ['offline'],
              multiplier: 1.5,
            },
          ],
        },
        config: null,
      },
      parent: null,
      totalTasks: null,
      metadata: {
        productFileName:
          salesTargetData?.selectedType === Constants.UploadedProducts
            ? salesTargetData?.tabWiseData?.child?.mediaFile[0]
            : '',
        isProductIdsUploaded: salesTargetData?.selectedType === Constants.UploadedProducts,
      },
    },
    application: null,
    parent: null,
    totalTasks: null,
  }
}
